import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import U2000BasicTable, { type U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { type U2000CriteriaLookupConfig } from "../../u2000Core/components/tables/basic/U2000BasicTableList";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { useU2000QuickSearchListEffect } from "../../u2000Core/hooks/useU2000QuickSearchListEffect";
import { u2000BuildQuickSearchFormPath, u2000ShouldQuickSearchOpenForm } from "../../u2000Core/tools/U2000QuickSearchUtils";
import {
  U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT,
  U2000_CRITERIA_LOOKUP_U3139_FIND_EMPLOYEE,
  U2000_CRITERIA_LOOKUP_U3139_FIND_SEARCH_CADASTRES,
  defaultItemToDisplay,
  defaultItemToSelectedDisplay,
  defaultItemToValue,
  registerCriteriaLookup,
  unregisterCriteriaLookup,
} from "../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry";
import {
  U2000_CRITERIA_INIT_OPTIONS_U3139_FAMILIES,
  U2000_CRITERIA_INIT_OPTIONS_U3139_LEGACY_WORK_NATURE_CODES,
  U2000_CRITERIA_INIT_OPTIONS_U3139_WORK_NATURES,
  U2000_CRITERIA_INIT_OPTIONS_U3139_WORK_NATURES_ID,
  registerCriteriaInitOptionsResolver,
  unregisterCriteriaInitOptionsResolver,
} from "../../u2000Core/components/tables/commons/search/U2000CriteriaInitOptionsRegistry";
import { buildU3155FamilyCriteriaOptions } from "../../u3155MinutesWorkNature/tools/U3155FamilyCriteria";
import type { U2000TableHandlers } from "../../u2000Core/types/U2000TableHandlers";
import { useU3121FindProjectHandlers } from "../../u3121Project/U3121UseFindHandlers";
import { useU3139FindEmployeeHandlers } from "../U3139UseFindEmployeeHandlers";
import {
  u3139FindSearchCadastreItemToDisplay,
  useU3139FindSearchCadastresHandlers,
} from "./list/U3139UseFindSearchHandlers";
import { useU3139ListDeleteHandlers, useU3139SearchHandlers } from "../U3139UseApiHandlers";
import { u2000FormatEmployeeLookupLabel } from "../../u2000Core/tools/U2000EmployeeDisplayHelpers";
import {
  luHandleAppiError,
  U3139_FIND_EMPLOYEE_INCLUDE_INACTIVE_CRITERION,
  type U3139FindEmployeeItem,
  type U3139MinutesSearchRow,
} from "../U3139Service";
import {
  U3121_FIND_PROJECT_INCLUDE_INACTIVE_CRITERION,
  u3121FormatProjectLookupLabel,
} from "../../u3121Project/U3121Service";
import { U3139MinutesListToolBar } from "./list/U3139MinutesListToolBar";
import { buildU3139WorkNatureCriteriaOptions, buildU3139WorkNatureIdCriteriaOptions } from "./list/U3139WorkNatureCriteria";
import { buildU3139LegacyWorkNatureCriteriaOptions } from "../tools/u3139LegacyWorkNatureConcordance";
import { getU3139MinutesListColumnsDef } from "./list/U3139MinutesListColumns";
import { NS } from "../U3139";
import { useU3139Context } from "../U3139Context";
import { useU3139InitCreateMinuteDialog } from "../U3139InitCreateMinuteDialogContext";
import U3139MinutesDeleteDlg from "./common/U3139MinutesDeleteDlg";

function u3139EmployeeLookupItemToDisplay(item: unknown, _config: U2000CriteriaLookupConfig, inactiveSuffix: string): string {
  return u2000FormatEmployeeLookupLabel(item as U3139FindEmployeeItem, inactiveSuffix, 'name-only');
}

function u3139EmployeeLookupItemToSelectedDisplay(
  item: unknown,
  _config: U2000CriteriaLookupConfig,
  inactiveSuffix: string,
): string {
  return u2000FormatEmployeeLookupLabel(item as U3139FindEmployeeItem, inactiveSuffix, 'name-only');
}

function u3139ProjectLookupItemToDisplay(item: unknown, _config: U2000CriteriaLookupConfig, inactiveSuffix: string): string {
  return u3121FormatProjectLookupLabel(item as Record<string, unknown>, inactiveSuffix);
}

function u3139ProjectLookupItemToSelectedDisplay(
  item: unknown,
  config: U2000CriteriaLookupConfig,
  inactiveSuffix: string,
): string {
  return u3139ProjectLookupItemToDisplay(item, config, inactiveSuffix);
}

const U3139MinutesList: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { initLU, luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3139Context();
  const { openAddDialog } = useU3139InitCreateMinuteDialog();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);
  const findProjectHandlers = useU3121FindProjectHandlers();
  const findEmployeeHandlers = useU3139FindEmployeeHandlers();
  const findSearchCadastresHandlers = useU3139FindSearchCadastresHandlers();

  useEffect(() => {
    const inactiveProjectSuffix = t("valInactive", { ns: NS });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT, {
      search: (criterias) =>
        findProjectHandlers.search([...criterias, U3121_FIND_PROJECT_INCLUDE_INACTIVE_CRITERION]),
      itemToValue: defaultItemToValue,
      itemToDisplay: (item, config) => u3139ProjectLookupItemToDisplay(item, config, inactiveProjectSuffix),
      itemToSelectedDisplay: (item, config) =>
        u3139ProjectLookupItemToSelectedDisplay(item, config, inactiveProjectSuffix),
    });
    const inactiveEmployeeSuffix = t("valEmployeeInactive", { ns: NS });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3139_FIND_EMPLOYEE, {
      search: (criterias) =>
        findEmployeeHandlers.search([...criterias, U3139_FIND_EMPLOYEE_INCLUDE_INACTIVE_CRITERION]),
      itemToValue: defaultItemToValue,
      itemToDisplay: (item, config) => u3139EmployeeLookupItemToDisplay(item, config, inactiveEmployeeSuffix),
      itemToSelectedDisplay: (item, config) =>
        u3139EmployeeLookupItemToSelectedDisplay(item, config, inactiveEmployeeSuffix),
    });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3139_FIND_SEARCH_CADASTRES, {
      search: findSearchCadastresHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: (item, config) => u3139FindSearchCadastreItemToDisplay(item) || defaultItemToDisplay(item, config),
      itemToSelectedDisplay: (item, config) => u3139FindSearchCadastreItemToDisplay(item) || defaultItemToSelectedDisplay(item, config),
    });
    registerCriteriaInitOptionsResolver(U2000_CRITERIA_INIT_OPTIONS_U3139_WORK_NATURES, () =>
      buildU3139WorkNatureCriteriaOptions(i18n.language, initLU.workNatures),
    );
    registerCriteriaInitOptionsResolver(U2000_CRITERIA_INIT_OPTIONS_U3139_WORK_NATURES_ID, () =>
      buildU3139WorkNatureIdCriteriaOptions(i18n.language, initLU.workNatures),
    );
    registerCriteriaInitOptionsResolver(U2000_CRITERIA_INIT_OPTIONS_U3139_FAMILIES, () =>
      buildU3155FamilyCriteriaOptions(i18n.language, initLU.families),
    );
    registerCriteriaInitOptionsResolver(U2000_CRITERIA_INIT_OPTIONS_U3139_LEGACY_WORK_NATURE_CODES, () =>
      buildU3139LegacyWorkNatureCriteriaOptions(),
    );
    return () => {
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT);
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3139_FIND_EMPLOYEE);
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3139_FIND_SEARCH_CADASTRES);
      unregisterCriteriaInitOptionsResolver(U2000_CRITERIA_INIT_OPTIONS_U3139_WORK_NATURES);
      unregisterCriteriaInitOptionsResolver(U2000_CRITERIA_INIT_OPTIONS_U3139_WORK_NATURES_ID);
      unregisterCriteriaInitOptionsResolver(U2000_CRITERIA_INIT_OPTIONS_U3139_FAMILIES);
      unregisterCriteriaInitOptionsResolver(U2000_CRITERIA_INIT_OPTIONS_U3139_LEGACY_WORK_NATURE_CODES);
    };
  }, [
    findProjectHandlers.search,
    findEmployeeHandlers.search,
    findSearchCadastresHandlers.search,
    i18n.language,
    initLU.workNatures,
    initLU.families,
    t,
  ]);

  useEffect(() => {
    const columns = getU3139MinutesListColumnsDef(t, i18n.language, initLU.workNatures, initLU.families);
    searchStateHandlers.setColumns(columns);
    gridStateHandlers.setColumns(columns);
    setLogicalUnitBreadcrumb([{ label: t("title", { ns: NS }) }]);
  }, [t, i18n.language, initLU.workNatures, initLU.families]);

  const handleSearch = useU3139SearchHandlers<U3139MinutesSearchRow>({
    onSuccess: (searchResult: U2000SearchResult<U3139MinutesSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());

      const qsSession = consumeQsSessionRef.current();
      const isFromQuickSearch = qsSession?.isFromQuickSearch === true;
      const targetLU = qsSession?.targetLogicalUnit ?? "/U3139";
      if (
        u2000ShouldQuickSearchOpenForm(window.location.pathname, targetLU) &&
        searchResult.data?.length === 1 &&
        isFromQuickSearch
      ) {
        const single = searchResult.data[0];
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        luStateHandlers.setActionOriginFrom("");
        const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === single.id);
        if (currentIdx !== -1) gridStateHandlers.setCurrentIdx(currentIdx);
        navigate(u2000BuildQuickSearchFormPath(targetLU, single.id), { replace: true });
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleDelete = useU3139ListDeleteHandlers({
    getSelectedRows: () => gridStateHandlers.getSelectedRows() as (U3139MinutesSearchRow & { id: string })[],
    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();
      deletedRows.forEach((row) => {
        const minuteRow = row as U3139MinutesSearchRow;
        toast.success(t("success0003", { key: minuteRow.minuteNumber ?? minuteRow.id, ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((r) => r.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);
      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: "/U3139",
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  const handlerActions: U2000TableHandlers<U3139MinutesSearchRow> = {
    onSearch: (page, direction) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (row) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom("");
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      if (currentIdx !== -1) gridStateHandlers.setCurrentIdx(currentIdx);
      navigate(`${row.id}`);
    },
    onAdd: () => openAddDialog(),
    onModify: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom("/U3139");
      navigate(`${gridStateHandlers.getSelectedRows()[0]?.id}`);
    },
    onDelete: () => handleDelete.handleDelete(),
  };

  if (searchStateHandlers.getColumns().length === 0) return null;

  return (
    <>
      <U3139MinutesDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers as any}
        actionsButtons={<U3139MinutesListToolBar gridStateHandlers={gridStateHandlers as any} handlerActions={handlerActions as any} />}
        handlerActions={handlerActions as any}
        label={t("title", { count: gridStateHandlers.getTotalRows(), ns: NS })}
        allowRowSelection
      />
    </>
  );
};

export default U3139MinutesList;
